import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Portal from "@mui/material/Portal";

import { TermSheetV2 } from "@toorak/op-web-common-service";

import { sanitizeCurrency, sanitizePercentage } from "../../utils/Common";
import { RootContext } from "../../context/RootContext";
import TermSheetMenu from "./TermSheetMenu";
import { useTermSheetActions } from "../../hooks/useTermSheetActions";
import DisclaimerPopupWithButtons from "../common/DisclaimerPopupWithButtons";

export default function TermSheetForm(props: any) {
  const {
    evaluationID = "",
    handleSaveApplication,
    isApplicationSubmitted,
    isTermSheetBlocked = false,
  } = props;

  const [termSheetAnchorEl, setTermSheetAnchorEl] =
    useState<null | HTMLElement>(null);

  const {
    termSheetData,
    setTermSheetData,
    termSheetConfig,
    termSheetVersions,
    termSheetAction,
    setTermSheetAction,
    currentTermSheetVersion,
    setCurrentTermSheetVersion,
    isOpenTermSheet,
    setIsOpenTermSheet,
  } = useContext(RootContext);

  const { t } = useTranslation();

  const {
    openTermSheetMenu,
    setOpenTermSheetMenu,
    isEmailSentOfCurrentVersion,
    renameTermSheetHandler,
    isTermsheetAlreadySaved,
    setIsTermsheetAlreadySaved,
    isDataEdited,
    setIsDataEdited,
    isSendingEmail,
    triggeredSendEmail,
    emailBodyChange,
    showDisclaimer,
    downloadDisclaimerText,
    containedButtonHandler,
    outlinedButtonHandler,
    closeDownloadDisclaimer,
    getTermSheetVersion,
    setShouldRefetch,
  } = useTermSheetActions({ evaluationID, interactive: true });

  const permissionsToRenderInUI = {
    showLoanDetails: true,
    showFeeDetails: true,
    showEmailSection: true,
    showEmailBody: true,
    isFullView: true,
    showMerchantsMortgage: false,
    showRequirements: true,
    showFooterActions: true,
    showDefaultSignature: false,
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setTermSheetAnchorEl(event.currentTarget);
    setOpenTermSheetMenu(true);
    setTermSheetAction("");
    setIsOpenTermSheet(false);
  };

  const handleClose = () => {
    setTermSheetAnchorEl(null);
    setOpenTermSheetMenu(false);
    setIsOpenTermSheet(false);
    setTermSheetAction("");
    setIsTermsheetAlreadySaved(false);
    getTermSheetVersion();
    setIsDataEdited(false);
    setTermSheetData({});
    setShouldRefetch(false);
  };

  return (
    <div>
      <Grid item className="gray-border">
        <Button
          className="mr-12"
          variant="outlined"
          onClick={handleClick}
          data-testid="create-term-sheet-button"
          disabled={isTermSheetBlocked}
          title={isTermSheetBlocked ? t("termSheetBlockedCapitalMarketReview") : undefined}
          endIcon={<KeyboardArrowDownIcon />}
        >
          {t("termSheet")}
        </Button>
        {openTermSheetMenu ? (
          <TermSheetMenu
            openTermSheetMenu={openTermSheetMenu}
            termSheetAnchorEl={termSheetAnchorEl}
            setIsOpenTermSheet={setIsOpenTermSheet}
            termSheetConfig={termSheetConfig}
            handleClose={handleClose}
            termSheetAction={termSheetAction}
            setTermSheetAction={setTermSheetAction}
            termSheetVersionsData={termSheetVersions}
            currentTermSheetVersion={currentTermSheetVersion}
            setCurrentTermSheetVersion={setCurrentTermSheetVersion}
            isEmailSentOfCurrentVersion={isEmailSentOfCurrentVersion}
            renameTermSheetHandler={renameTermSheetHandler}
            handleSaveApplication={handleSaveApplication}
            isApplicationSubmitted={isApplicationSubmitted}
          />
        ) : null}
      </Grid>
      {isOpenTermSheet ? (
        <TermSheetV2
          isOpenTermSheet={isOpenTermSheet}
          termSheetData={termSheetData}
          setTermSheetData={setTermSheetData}
          isUserConfig={false}
          permissionsToRenderInUI={permissionsToRenderInUI}
          onClose={handleClose}
          termSheetConfig={termSheetConfig}
          triggeredSendEmail={triggeredSendEmail}
          emailBodyChange={emailBodyChange}
          sanitizeCurrency={sanitizeCurrency}
          sanitizePercentage={sanitizePercentage}
          isTermsheetAlreadySaved={isTermsheetAlreadySaved}
          isDataEdited={isDataEdited}
        />
      ) : null}
      {isSendingEmail && (
        // TermSheetV2 (external package) renders its own content inside an
        // MUI Modal, which portals to document.body. A Backdrop rendered
        // inline here would stay wherever TermSheetForm sits in the app's own
        // DOM tree, so no z-index value could reliably put it above that
        // portaled modal. Portal escapes to document.body too, landing after
        // the modal's portal node (mounted only once isSendingEmail flips to
        // true, i.e. after the modal is already open) so it stacks on top.
        <Portal>
          <Backdrop
            open={isSendingEmail}
            sx={{
              zIndex: (theme) => theme.zIndex.modal + 1,
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <CircularProgress color="inherit" />
            <Typography>Sending Term Sheet...</Typography>
          </Backdrop>
        </Portal>
      )}
      {showDisclaimer && (
        <DisclaimerPopupWithButtons
          openDisclaimer={showDisclaimer}
          disclaimerHeading={"Disclaimer"}
          disclaimerText={downloadDisclaimerText}
          containedButtonLabel={`Save & Download`}
          containedButtonHandler={containedButtonHandler}
          outlinedButtonLabel={`Download Only`}
          outlinedButtonHandler={outlinedButtonHandler}
          closeDisclaimer={closeDownloadDisclaimer}
        />
      )}
    </div>
  );
}
